import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import "moment-timezone";
import { fetchGETRequest } from "../utils/services";
import { LiveDataRecord } from "../types";
import { transformV2Record } from "../utils/meterDataMapper";

// Types
interface RegisterType {
  value: string;
  type: string;
}

interface TimeStamp {
  start: number;
  end: number;
}

interface ChartData {
  xAxis: string[];
  yAxis: (string | number)[];
  legend: string;
}

interface QueryResponse {
  [key: string]: Array<{
    ts: number;
    value: string | number;
  }>;
}

interface UseReadClusterProps {
  meterId: string;
  registerType: RegisterType;
  timeStamp: TimeStamp;
  rowsPerPage: number;
  profileType?: string;
}

interface FetchResult {
  transformedLiveData: LiveDataRecord[];
  chartData: ChartData;
}


const fetchReadCluster = async ({
  meterId,
  registerType,
  timeStamp,
  rowsPerPage,
  profileType,
}: {
  meterId: string;
  registerType: RegisterType;
  timeStamp: TimeStamp;
  rowsPerPage: number;
  profileType?: string;
}): Promise<FetchResult> => {
  if (!meterId)
    return {
      transformedLiveData: [],
      chartData: {
        xAxis: [],
        yAxis: [],
        legend: "",
      },
    };

  const fromDate = moment(timeStamp.start).format("YYYY-MM-DDTHH:mm");
  const toDate = moment(timeStamp.end).format("YYYY-MM-DDTHH:mm");

  let v2Type = "instant";
  if (profileType === "Load Profile") v2Type = "load";
  else if (profileType === "Daily") v2Type = "daily";
  else if (profileType === "Billing") v2Type = "billing";

  const response = await fetchGETRequest<any>(
    `v2/meter-reads/browse?type=${v2Type}&skip=0&take=${rowsPerPage}&search=${meterId}&from=${fromDate}&to=${toDate}`,
  );

  const responseData =
    response?.data?.records ||
    response?.records ||
    (Array.isArray(response?.data) ? response?.data : []) ||
    (Array.isArray(response) ? response : []);

  const transformedLiveData: LiveDataRecord[] = responseData.map(
    (record: any) => {
      const { ts, val, readingTime } = transformV2Record(
        record,
        registerType.value,
      );

      return {
        currentDateTime: !isNaN(ts)
          ? moment.utc(ts).format("YYYY-MM-DDTHH:mm:ssZ")
          : "Invalid Date",
        readingTime: readingTime ?? "",
        currentRead: val ?? "-",
        currentReadQuality: "Good",
        readId: registerType.value,
        ts: isNaN(ts) ? 0 : ts,
      };
    },
  );

  const sortedData = [...transformedLiveData].sort((a, b) => a.ts - b.ts);

  const chartData: ChartData = {
    xAxis: sortedData.map((item) =>
      moment(item.ts).format("DD/MM/YYYY HH:mm:ss"),
    ),
    yAxis: sortedData.map((item) => item.currentRead),
    legend: registerType?.type,
  };

  return { transformedLiveData, chartData };
};

const useReadCluster = ({
  meterId,
  registerType,
  timeStamp,
  rowsPerPage,
  profileType,
}: UseReadClusterProps) => {
  return useQuery({
    // Include params in the key so cache is scoped correctly and retained across tab switches
    queryKey: [
      "readCluster",
      meterId,
      registerType?.value,
      timeStamp?.start,
      timeStamp?.end,
      rowsPerPage,
      profileType || "",
    ],
    queryFn: () =>
      fetchReadCluster({
        meterId,
        registerType,
        timeStamp,
        rowsPerPage,
        profileType: profileType || "",
      }),
    enabled: !!meterId && !!registerType && !!timeStamp,
    // Prevent automatic refetches when simply remounting/visiting the tab again
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Keep previous data while params change to avoid UI flicker
    // Consider data always fresh; only manual refetch or param changes will refetch
    staleTime: Infinity,
    // Keep cache for 30 minutes to persist data across tab switches
    gcTime: 30 * 60 * 1000,
    refetchInterval: false,
    retry: false,
  });
};

export default useReadCluster;
